import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { http, apiError, API } from "@/lib/api";

const SUGGESTIONS = [
  "What AI models are we using right now?",
  "How does upload screening decide drawing vs photo?",
  "Why might a photo get rejected as an object?",
  "Summarize the recent screening flags.",
];

function sessionId() {
  let s = localStorage.getItem("admin_assistant_session");
  if (!s) {
    s = `admin-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("admin_assistant_session", s);
  }
  return s;
}

export function AdminAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const sid = useRef(sessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && !loaded) {
      http
        .get(`/admin/assistant/history?session_id=${encodeURIComponent(sid.current)}`)
        .then((r) => setMessages(r.data.messages || []))
        .catch(() => {})
        .finally(() => setLoaded(true));
    }
  }, [open, loaded]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  async function send(text) {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/assistant/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid.current, message }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let gotError = null;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          const t = line.trim();
          if (!t.startsWith("data:")) continue;
          let payload;
          try {
            payload = JSON.parse(t.slice(5).trim());
          } catch {
            continue;
          }
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              if (last && last.role === "assistant") copy[copy.length - 1] = { ...last, content: last.content + payload.delta };
              return copy;
            });
          } else if (payload.error) {
            gotError = payload.error;
          }
        }
      }
      if (gotError) {
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          if (last && last.role === "assistant" && !last.content) copy[copy.length - 1] = { ...last, content: `⚠️ ${gotError}` };
          return copy;
        });
      }
    } catch (e) {
      toast.error(apiError(e));
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant" && !last.content)
          copy[copy.length - 1] = { ...last, content: "⚠️ I couldn't answer that just now. Please try again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  }

  async function clearChat() {
    try {
      await http.delete(`/admin/assistant/history?session_id=${encodeURIComponent(sid.current)}`);
      setMessages([]);
      toast.success("Chat cleared.");
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="admin-assistant-open"
          className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-[#4A235A] px-5 py-3.5 font-bold text-white shadow-[0_10px_30px_rgba(74,35,90,0.4)] transition-transform hover:-translate-y-0.5"
        >
          <Sparkles size={18} className="text-[#F4D03F]" /> Ask Luna
        </button>
      )}

      {open && (
        <div
          data-testid="admin-assistant-panel"
          className="fixed bottom-5 right-5 z-50 flex h-[min(600px,80vh)] w-[min(420px,92vw)] flex-col overflow-hidden rounded-3xl border border-black/10 bg-white shadow-[0_20px_60px_rgba(74,35,90,0.35)]"
        >
          <div className="flex items-center justify-between bg-[#4A235A] px-4 py-3 text-white">
            <div className="flex items-center gap-2">
              <Sparkles size={18} className="text-[#F4D03F]" />
              <div>
                <p className="text-sm font-bold leading-tight">Luna — AI Ops Assistant</p>
                <p className="text-[11px] leading-tight text-white/70">Ask what's going on with the AI</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={clearChat} data-testid="admin-assistant-clear" title="Clear chat" className="rounded-lg p-1.5 hover:bg-white/10">
                <Trash2 size={16} />
              </button>
              <button onClick={() => setOpen(false)} data-testid="admin-assistant-close" title="Close" className="rounded-lg p-1.5 hover:bg-white/10">
                <X size={18} />
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-[#F7F5F9] p-4" data-testid="admin-assistant-messages">
            {messages.length === 0 && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-500">
                  Hi! I can explain how the AI screening, likeness rendering and prompts are working. Try one:
                </p>
                <div className="flex flex-col gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      data-testid="admin-assistant-suggestion"
                      className="rounded-2xl border border-[#4A235A]/15 bg-white px-3 py-2 text-left text-sm font-semibold text-[#4A235A] transition-colors hover:border-[#9B5DE5] hover:bg-[#9B5DE5]/5"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`admin-assistant-msg-${m.role}`}
                  className={`max-w-[85%] whitespace-pre-line rounded-2xl px-3.5 py-2.5 text-sm font-medium ${
                    m.role === "user" ? "bg-[#4A235A] text-white" : "border border-black/5 bg-white text-[#1A0F1C]"
                  }`}
                >
                  {m.content || (
                    <span className="inline-flex items-center gap-2 text-gray-500">
                      <Loader2 size={16} className="animate-spin text-[#9B5DE5]" /> Luna is thinking…
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-end gap-2 border-t border-black/5 bg-white p-3">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={1}
              placeholder="Ask about the AI… (Enter to send)"
              data-testid="admin-assistant-input"
              className="max-h-28 flex-1 resize-none rounded-2xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#9B5DE5] focus:ring-2 focus:ring-[#F4D03F]/40"
            />
            <button
              onClick={() => send()}
              disabled={busy || !input.trim()}
              data-testid="admin-assistant-send"
              className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[#F4D03F] text-[#1A0F1C] transition-transform hover:-translate-y-0.5 disabled:opacity-50"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
